"use client";

import { AppModal, Button, Typography } from "@/components/ui";
import { CurrencyWrapper } from "@/components/ui/CurrencyWrapper/CurrencyWrapper";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { formatUsersName } from "@/lib/utils/format";
import type { MedicalTreatmentBalanceConfirmation } from "@/lib/types";
import { calculateMedicalTreatmentBillTotals } from "@/lib/utils/matters/medical-treatment-bill-utils";
import { useSuspenseQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { CircleCheck, CircleX, Pencil } from "lucide-react";
import { useMemo } from "react";

interface BalanceConfirmationHistoryModalProps {
  title: string;
  description?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  matterId: string;
  treatmentId?: string;
}

export const BalanceConfirmationHistoryModal = ({
  title,
  description,
  open,
  setOpen,
  matterId,
  treatmentId,
}: BalanceConfirmationHistoryModalProps) => {
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const { hasActionPermission } = useUser();
  const { openTreatmentBalanceConfirmationModal } = useGlobalModal();
  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentBalanceConfirmation,
    ACTION_PERMISSION_VALUES.update,
  );
  const treatmentBills = treatmentId
    ? matterData.data.medicalTreatmentBills.filter(
        (bill) => bill.medicalTreatmentId === treatmentId,
      )
    : [];
  const { totalBill } = calculateMedicalTreatmentBillTotals(treatmentBills);
  const canConfirmBalance = hasUpdatePermission && Boolean(treatmentId);

  const confirmations = matterData.data.medicalTreatmentBalanceConfirmations
    .filter(
      (confirmation) =>
        !treatmentId || confirmation.medicalTreatmentId === treatmentId,
    )
    .slice()
    .sort((a, b) => {
      const aDate = a.date ?? a.dateCreated;
      const bDate = b.date ?? b.dateCreated;
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return dayjs(bDate).diff(dayjs(aDate));
    });
  const latestConnectionByConfirmationId = useMemo(() => {
    const connections = matterData.data.userConnections;
    const map = new Map<string, (typeof connections)[number]>();
    connections.forEach((connection) => {
      const confirmationId = connection.medicalTreatmentBalanceConfirmationId;
      if (!confirmationId) return;
      const existing = map.get(confirmationId);
      if (
        !existing ||
        dayjs(connection.dateCreated).isAfter(existing.dateCreated)
      ) {
        map.set(confirmationId, connection);
      }
    });
    return map;
  }, [matterData.data.userConnections]);
  const formatConfirmedByLabel = (confirmationId?: string) => {
    if (!confirmationId) return;
    const connection = latestConnectionByConfirmationId.get(confirmationId);
    if (!connection) return;
    const userName = formatUsersName(connection)?.trim();
    return userName || undefined;
  };

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      title={title}
      description={description}
      size="xl"
      footer={
        canConfirmBalance ? (
          <div className="flex justify-end">
            <Button
              variant="default"
              onClick={() => {
                openTreatmentBalanceConfirmationModal({
                  matterId,
                  treatmentId,
                  initialData: {
                    confirmed: true,
                    amount: totalBill,
                    date: dayjs().format("YYYY-MM-DD"),
                  },
                });
              }}
            >
              Create new confirmation
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="min-w-0">
        {confirmations.length === 0 ? (
          <div className="border-border flex items-center gap-2 rounded-md border bg-red-50 p-3 text-red-600">
            <CircleX className="h-4 w-4" />
            <Typography size="sm" className="text-red-600">
              Not confirmed
            </Typography>
          </div>
        ) : (
          <div className="space-y-3">
            {confirmations.map((confirmation) => (
              <div
                key={confirmation.id}
                className="border-border flex items-center gap-3 rounded-md border p-3"
              >
                <div className="flex flex-1 flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-xs">
                      {formatConfirmationDate(confirmation)}
                    </span>
                    {confirmation.confirmed ? (
                      <div data-testid="confirmed-icon">
                        <CircleCheck className="h-4 w-4 text-green-500" />
                      </div>
                    ) : (
                      <div data-testid="not-confirmed-icon">
                        <CircleX className="h-4 w-4 text-red-500" />
                      </div>
                    )}
                  </div>
                  {(() => {
                    const confirmedByLabel = formatConfirmedByLabel(
                      confirmation.id,
                    );
                    if (confirmation.confirmed) {
                      return (
                        <Typography size="sm">
                          {confirmedByLabel
                            ? `Confirmed by ${confirmedByLabel}`
                            : "Confirmed"}
                        </Typography>
                      );
                    }
                    return <Typography size="sm">Not confirmed</Typography>;
                  })()}
                </div>
                <div className="text-right">
                  <CurrencyWrapper value={confirmation.amount ?? undefined} />
                </div>
                {hasUpdatePermission && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    title="Edit balance confirmation"
                    data-testid="edit-balance-confirmation-button"
                    onClick={() => {
                      openTreatmentBalanceConfirmationModal({
                        matterId,
                        treatmentId,
                        editing: true,
                        initialData: confirmation,
                      });
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AppModal>
  );
};

const formatConfirmationDate = (
  confirmation: MedicalTreatmentBalanceConfirmation,
) => {
  const date = confirmation.date ?? confirmation.dateCreated;
  if (!date) return "Unknown date";
  return dayjs(date).format("MMM D, YYYY");
};
